import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowRight, ChevronRight, Mic2, ExternalLink, Linkedin, Menu, X, Mail } from 'lucide-react';

const companies = [
  {
    name: 'Spaza Global',
    tagline: 'Marketplace & Supply Chain',
    description: 'A curated trade corridor connecting Southern African producers with global buyers. Hospitality gifting, cultural retail, and corporate procurement — powered by resilient, trustworthy supply chains.',
    tags: [
      { label: 'Marketplace', url: 'https://spazaglobal.com/marketplace' },
      { label: 'Trade Expeditions', url: 'https://spazaglobal.com/trade-expeditions' },
    ],
    logo: '/spaza-global-logo.png',
    logoBg: '#0A0A0A',
    image: '/spaza-conference-bags.png',
    imageClass: 'object-contain',
    imageStyle: { backgroundColor: '#F5F0E8' },
    website: 'https://www.spazaglobal.com',
    linkLabel: '',
    email: 'hello@spazaglobal.com',
  },
  {
    name: 'Spaza Luxe Escapes',
    tagline: 'Luxury & Sustainable Travel',
    description: 'Authentic luxury in Lesotho & South Africa. Curated experiences with local expertise, responsible tourism, and cultural immersion — from wellness retreats to adventure expeditions.',
    tags: ['Leisure Travel', 'Bespoke Itineraries'],
    logo: '/spaza-luxe-escapes-logo.png',
    logoBg: '#FBF8F3',
    image: 'https://images.unsplash.com/photo-1667987566780-3b31fa5485c8?w=800&q=80',
    imageClass: 'object-center',
    website: 'https://www.spazaluxeescapes.com',
    linkLabel: 'Explore Escapes',
  },
  {
    name: 'Star Global Remit',
    tagline: 'Cross-Border Fintech',
    description: 'Fast, trusted and community-centred cross-border remittance connecting Southern Africa and the UK.',
    tags: ['Remittance', 'FX'],
    logo: '/sgr-logo.png',
    logoBg: '#FAFAFA',
    image: 'https://images.unsplash.com/photo-1544304067-0cc500bfa816?w=800&q=80',
    imageClass: 'object-center',
    website: 'https://www.starglobalremit.com/',
    linkLabel: 'Visit Platform',
  },
  {
    name: 'Star Talent Group',
    tagline: 'Media House & Tour Management',
    description: 'Amplifying voices across the diaspora through publishing, media and talent representation. An artist booking platform connecting talent with opportunity.',
    tags: ['STG Publishing', 'Tour Management'],
    logo: '/stg-logo.jpg',
    logoBg: '#1A2F4A',
    image: '/stg-lady-white-dress.png',
    imageClass: 'object-cover',
    imageStyle: { objectPosition: 'center 20%' },
    website: 'https://www.startalentgrp.com/',
    linkLabel: 'Visit Platform',
  },
  {
    name: 'KiddiPlay',
    tagline: 'Tech SaaS',
    description: 'A tech-based SaaS subscription model connecting families across the UK through curated experiences and discovery.',
    tags: ['Subscription Platform'],
    logo: '/kiddiplay-logo.jpg',
    logoBg: '#FFF5FB',
    image: 'https://images.unsplash.com/photo-1561049527-9743861dce35?w=800&q=80',
    imageClass: '',
    imageStyle: { objectPosition: 'center 40%' },
    website: 'https://www.kiddiplayclub.com/',
    linkLabel: 'Visit App',
  },
];

const LandingPage = () => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', system-ui, sans-serif" }} data-testid="landing-page">

      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-white/95 backdrop-blur-md" style={{ borderBottom: '1px solid #E8E0D4' }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 sm:gap-3 no-underline">
            <img src="/maluti-icon.png" alt="Maluti Holdings" className="w-7 h-7 sm:w-8 sm:h-8 object-contain" />
            <div>
              <span className="text-sm sm:text-base font-semibold tracking-[0.2em] uppercase" style={{ color: '#2C1A0E' }}>Maluti</span>
              <span className="text-sm sm:text-base font-light tracking-[0.2em] uppercase ml-1 hidden sm:inline" style={{ color: '#B8860B' }}>Holdings</span>
            </div>
          </Link>
          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-6 sm:gap-8">
            <a href="#portfolio" className="text-xs uppercase tracking-[0.15em] transition-colors duration-300 hover:text-[#B8860B] no-underline" style={{ color: '#8B7355' }}>Portfolio</a>
            <Link to="/vision" className="text-xs uppercase tracking-[0.15em] transition-colors duration-300 hover:text-[#B8860B] no-underline" style={{ color: '#8B7355' }}>Vision</Link>
            <Link to="/advisory" className="text-xs uppercase tracking-[0.15em] transition-colors duration-300 hover:text-[#B8860B] no-underline" style={{ color: '#8B7355' }}>Speaking</Link>
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 px-4 py-2 text-[10px] uppercase tracking-[0.15em] font-medium transition-all duration-300 hover:opacity-90"
              style={{ backgroundColor: '#8B7355', color: '#F5F0E8' }}
              data-testid="login-btn"
            >
              Ops Suite
            </button>
          </div>
          {/* Mobile hamburger */}
          <button className="md:hidden p-1" onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-btn">
            {menuOpen ? <X className="w-6 h-6" style={{ color: '#2C1A0E' }} /> : <Menu className="w-6 h-6" style={{ color: '#2C1A0E' }} />}
          </button>
        </div>
        {/* Mobile dropdown */}
        {menuOpen && (
          <div className="md:hidden px-6 pb-4 bg-white flex flex-col gap-3" style={{ borderTop: '1px solid #E8E0D4' }}>
            <Link to="/collaborations" onClick={() => setMenuOpen(false)} className="text-xs uppercase tracking-[0.15em] py-2 transition-colors hover:text-[#B8860B] no-underline" style={{ color: '#8B7355' }}>Portfolio</Link>
            <Link to="/vision" onClick={() => setMenuOpen(false)} className="text-xs uppercase tracking-[0.15em] py-2 transition-colors hover:text-[#B8860B] no-underline" style={{ color: '#8B7355' }}>Vision</Link>
            <Link to="/advisory" onClick={() => setMenuOpen(false)} className="text-xs uppercase tracking-[0.15em] py-2 transition-colors hover:text-[#B8860B] no-underline" style={{ color: '#8B7355' }}>Speaking & Advisory</Link>
            <button
              onClick={() => { navigate('/dashboard'); setMenuOpen(false); }}
              className="flex items-center gap-2 px-4 py-2 text-[10px] uppercase tracking-[0.15em] font-medium self-start"
              style={{ backgroundColor: '#8B7355', color: '#F5F0E8' }}
            >
              Ops Suite <ArrowRight className="w-3 h-3" />
            </button>
          </div>
        )}
      </nav>

      {/* Hero */}
      <section className="pt-28 sm:pt-32 pb-4 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-4xl">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-px" style={{ backgroundColor: '#B8860B' }} />
              <span className="text-[11px] uppercase tracking-[0.3em] font-semibold" style={{ color: '#B8860B' }}>Rooted in Southern African Heritage</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-light leading-[1.15] mb-3 tracking-tight" style={{ color: '#2C1A0E' }}>
              Harnessing culture, community & global connection
              <br />
              through <span className="font-semibold" style={{ color: '#B8860B' }}>business & technology.</span>
            </h1>
            <p className="text-sm sm:text-base leading-relaxed mb-3 max-w-2xl" style={{ color: '#6B5B4E' }}>
              Welcome to Maluti Holdings. We connect partners, investors and collaborators with a portfolio of ventures — built at the intersection of Southern African heritage, innovation, and luxury.
            </p>
            <p className="text-sm leading-relaxed mb-6 max-w-2xl" style={{ color: '#8B7355' }}>
              Spanning lifestyle, travel, supply chain, publishing, fintech, and tech — across Southern Africa and the UK.
            </p>
            <div className="flex flex-wrap gap-3">
              <a href="#portfolio" className="inline-flex items-center gap-2 px-7 py-3 text-xs uppercase tracking-[0.2em] font-medium transition-all duration-300 hover:opacity-90" style={{ backgroundColor: '#2C1A0E', color: '#F5F0E8' }} data-testid="explore-portfolio-btn">
                Explore Portfolio <ChevronRight className="w-4 h-4" />
              </a>
              <Link to="/advisory" className="inline-flex items-center gap-2 px-7 py-3 text-xs uppercase tracking-[0.2em] font-medium border-2 transition-all duration-300 hover:bg-[#2C1A0E] hover:text-white hover:border-[#2C1A0E] no-underline" style={{ borderColor: '#B8860B', color: '#B8860B' }} data-testid="speaking-btn">
                <Mic2 className="w-4 h-4" /> Speaking & Advisory
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Meet the Founder */}
      <section className="py-4 px-6 mt-3" style={{ backgroundColor: '#2C1A0E' }}>
        <div className="max-w-7xl mx-auto">
          <p className="text-[9px] uppercase tracking-[0.3em] mb-3 text-center sm:text-left" style={{ color: '#D4AF37' }}>Meet the Founder</p>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <img src="/naledi-photo.jpg" alt="Naledi McCarthy-Ocansey" className="w-10 h-10 rounded-full object-cover border-2" style={{ borderColor: '#D4AF37' }} />
            <div>
              <p className="text-sm font-medium tracking-wide" style={{ color: '#F5F0E8' }}>Naledi McCarthy-Ocansey, MSc Int'l Business, PG Dip</p>
              <p className="text-xs tracking-wide" style={{ color: '#C4B5A5' }}>Founder & CEO — Cultural Branding & Business Architect</p>
            </div>
            <a href="https://www.linkedin.com/in/naledimccarthy/" target="_blank" rel="noopener noreferrer" className="transition-opacity hover:opacity-70 ml-1" data-testid="linkedin-link">
              <Linkedin className="w-4 h-4" style={{ color: '#D4AF37' }} />
            </a>
          </div>
          <div className="flex items-center gap-5">
            {[['4', 'Brands'], ['2', 'Continents'], ['1', 'Ecosystem']].map(([val, label], i) => (
              <React.Fragment key={label}>
                {i > 0 && <div className="w-px h-7" style={{ backgroundColor: 'rgba(255,255,255,0.15)' }} />}
                <div className="text-center">
                  <p className="text-lg font-light" style={{ color: '#D4AF37' }}>{val}</p>
                  <p className="text-[9px] uppercase tracking-wider" style={{ color: '#C4B5A5' }}>{label}</p>
                </div>
              </React.Fragment>
            ))}
          </div>
          </div>
          <p className="text-xs italic font-light mt-3 text-center sm:text-left" style={{ color: '#C4B5A5' }}>
            <span style={{ color: '#D4AF37' }}>&ldquo;</span>Connecting people, trade, and technology—creating opportunities for us all to shape the global story<span style={{ color: '#D4AF37' }}>&rdquo;</span>
          </p>
        </div>
      </section>

      {/* Trusted By / Brand Logo Banner */}
      <section className="py-4 px-6" style={{ backgroundColor: '#FAF7F2', borderTop: '1px solid #E8E0D4', borderBottom: '1px solid #E8E0D4' }}>
        <div className="max-w-6xl mx-auto">
          <p className="text-[9px] uppercase tracking-[0.3em] text-center mb-4 font-semibold" style={{ color: '#B8860B' }}>Trusted By & Worked With</p>
          <div className="flex items-center justify-center gap-5 sm:gap-7 md:gap-9 flex-wrap">
            {[
              { src: '/logo-sa-tourism.png', alt: 'South African Tourism' },
              { src: '/logo-atta.svg', alt: 'ATTA' },
              { src: '/logo-bbc-1xtra.png', alt: 'BBC 1Xtra' },
              { src: '/logo-sampra.jpeg', alt: 'SAMPRA' },
              { src: '/logo-wtm.webp', alt: 'WTM London' },
              { src: '/logo-concerts-sa.webp', alt: 'Concerts SA', cover: true },
              { src: '/logo-henley.png', alt: 'Henley Business School' },
              { src: '/logo-the-shard.svg', alt: 'The Shard London' },
              { src: '/logo-ministry-of-sound.png', alt: 'Ministry of Sound' },
              { src: '/logo-chelsea-fc.png', alt: 'Chelsea FC', cover: true },
            ].map(({ src, alt, cover }) => (
              <div key={alt} className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-white border flex items-center justify-center overflow-hidden flex-shrink-0" style={{ borderColor: '#E8E0D4' }}>
                <img src={src} alt={alt} className={`${cover ? 'w-full h-full object-cover' : 'w-[80%] h-[80%] object-contain'}`} />
              </div>
            ))}
          </div>
          <div className="text-right mt-3">
            <Link to="/collaborations" className="text-[10px] font-medium tracking-wider no-underline hover:underline" style={{ color: '#B8860B' }}>and more &rarr;</Link>
          </div>
        </div>
      </section>

      {/* Portfolio */}
      <section id="portfolio" className="pt-12 pb-6 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-px" style={{ backgroundColor: '#B8860B' }} />
              <span className="text-[11px] uppercase tracking-[0.3em] font-semibold" style={{ color: '#B8860B' }}>The Portfolio</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-light tracking-tight" style={{ color: '#2C1A0E' }}>Our Companies</h2>
          </div>
          <div className="flex flex-col">
            {companies.map((company, index) => (
              <PortfolioRow key={company.name} company={company} isLast={index === companies.length - 1} />
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6" style={{ backgroundColor: '#2C1A0E' }}>
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <img src="/maluti-icon.png" alt="Maluti Holdings" className="w-5 h-5 object-contain" />
            <span className="text-xs tracking-[0.15em] uppercase" style={{ color: '#8B7355' }}>&copy; {new Date().getFullYear()} Maluti Holdings</span>
          </div>
          <div className="flex items-center gap-5 sm:gap-7">
            <a href="mailto:hello@malutiholdings.com" className="text-xs transition-colors duration-300 hover:text-white" style={{ color: '#C4B5A5' }}>hello@malutiholdings.com</a>
            <a href="https://www.linkedin.com/in/naledimccarthy/" target="_blank" rel="noopener noreferrer" className="transition-opacity hover:opacity-70"><Linkedin className="w-4 h-4" style={{ color: '#D4AF37' }} /></a>
            <button onClick={() => navigate('/dashboard')} className="text-xs uppercase tracking-[0.15em] font-medium transition-colors duration-300 hover:text-white" style={{ color: '#D4AF37' }} data-testid="footer-ops-link">Ops Suite</button>
          </div>
        </div>
      </footer>
    </div>
  );
};

const PortfolioRow = ({ company, isLast }) => (
  <div className="flex flex-col md:flex-row items-stretch md:max-h-[340px]" style={{ borderBottom: isLast ? 'none' : '1px solid #E8E0D4' }} data-testid={`portfolio-card-${company.name.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="w-full md:w-1/2 py-7 md:py-8 pr-6 md:pr-12 flex flex-col justify-center order-2 md:order-1">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-11 h-11 rounded-full overflow-hidden flex-shrink-0 shadow-sm border-2" style={{ borderColor: '#E8E0D4', backgroundColor: company.logoBg }}>
          <img src={company.logo} alt={company.name} className="w-full h-full object-cover" />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] font-semibold" style={{ color: '#B8860B' }}>{company.tagline}</p>
          <h3 className="text-lg font-semibold tracking-tight" style={{ color: '#2C1A0E' }}>{company.name}</h3>
        </div>
      </div>
      <p className="text-sm leading-[1.7] mb-4" style={{ color: '#6B5B4E' }}>{company.description}</p>
      <div className="flex flex-wrap items-center gap-1.5 mb-4">
        {company.tags.map(tag => {
          const label = typeof tag === 'string' ? tag : tag.label;
          const url = typeof tag === 'object' ? tag.url : null;
          const Tag = url ? 'a' : 'span';
          const extra = url ? { href: url, target: '_blank', rel: 'noopener noreferrer' } : {};
          return (
            <Tag key={label} {...extra} className={`text-[10px] px-3 py-1 tracking-wider uppercase font-medium inline-flex items-center gap-1.5 ${url ? 'hover:opacity-70 transition-opacity cursor-pointer' : ''}`} style={{ backgroundColor: '#F5F0E8', color: '#8B7355', border: '1px solid #E8E0D4' }}>
              {label}{url && <ExternalLink className="w-2.5 h-2.5" />}
            </Tag>
          );
        })}
        {company.email && (
          <a href={`mailto:${company.email}`} className="w-6 h-6 rounded-full flex items-center justify-center hover:opacity-70 transition-opacity" style={{ backgroundColor: '#F5F0E8', border: '1px solid #E8E0D4' }} title={company.email}>
            <Mail className="w-3 h-3" style={{ color: '#8B7355' }} />
          </a>
        )}
      </div>
      {company.website && company.linkLabel && (
        <a href={company.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] font-semibold transition-colors duration-300 hover:opacity-70 self-start" style={{ color: '#B8860B' }}>
          {company.linkLabel} <ExternalLink className="w-3.5 h-3.5" />
        </a>
      )}
    </div>
    <div className="w-full md:w-1/2 h-56 md:h-[340px] relative overflow-hidden group order-1 md:order-2 flex items-center justify-center p-6 md:p-10">
      <div className="relative w-56 h-56 md:w-64 md:h-64 rounded-full overflow-hidden border-4 shadow-lg" style={{ borderColor: '#D4AF37' }}>
        <img src={company.image} alt={company.name} className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 ${company.imageClass || ''}`} style={company.imageStyle || {}} />
        <div className="absolute inset-0 rounded-full" style={{ background: 'linear-gradient(135deg, rgba(212,175,55,0.15) 0%, rgba(44,26,14,0.2) 100%)', mixBlendMode: 'multiply' }} />
      </div>
    </div>
  </div>
);

export default LandingPage;
